// Related Products Carousel Custom Element
(function() {
  'use strict';
  
  // Prevent multiple registrations
  if (customElements.get('related-products-carousel')) {
    return;
  }

  class RelatedProductsCarousel extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.initialized = false;
    }

    connectedCallback() {
      if (this.initialized) return;
      
      // Wait for DOM to be ready
      setTimeout(() => {
        this.initializeCarousel();
      }, 100);
    }

    initializeCarousel() {
      if (this.initialized) return;
      
      // Get elements
      this.scroller = this.querySelector(".scroller");
      this.wrapper = this.scroller ? this.scroller.querySelector(".related_product_wrapper") : null;
      this.productItems = this.wrapper ? Array.from(this.wrapper.children) : [];
      this.prevButton = this.querySelector(".swiper-button-prev");
      this.nextButton = this.querySelector(".swiper-button-next");
      this.container = this.querySelector(".related_products_container");
      this.iterationCount = this.querySelector('.iteration_count');

      if (!this.scroller || !this.wrapper || this.productItems.length === 0) {
        return;
      }

      // Get settings from data attributes
      const cardBorders = this.dataset.cardBorders === 'true';
      const cardWidth = parseInt(this.dataset.cardWidth) || 90;
      const imageWidth = this.dataset.imageWidth || 'medium';
      const cardBackground = this.dataset.cardBackground || '';
      const cardBorder = this.dataset.cardBorder || '';

      // Apply styling
      this.applyCardStyling(cardBorders, cardBackground, cardBorder, imageWidth, cardWidth);

      // Set up event listeners
      this.setupEventListeners();

      // Set product widths
      this.setProductWidths(cardWidth);

      // Initial state
      this.updateButtonState();
      this.updateFadeOverlays();
      
      if (this.iterationCount) {
        this.iterationCount.innerText = `Number of products: ${this.productItems.length}`;
      }

      this.initialized = true;
    }

    setupEventListeners() {
      // Scroll event
      if (this.scroller) {
        this.scroller.addEventListener("scroll", () => {
          const childWidth = this.getChildWidth();
          if (childWidth > 0) {
            this.currentIndex = Math.round(this.scroller.scrollLeft / childWidth);
            this.updateButtonState();
            this.updateFadeOverlays();
          }
        });
      }

      // Button events
      if (this.prevButton) {
        this.prevButton.addEventListener('click', (e) => {
          e.preventDefault();
          this.scrollProducts('prev');
        });
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', (e) => {
          e.preventDefault();
          this.scrollProducts('next');
        });
      }

      // Recommendations loaded event
      this.addEventListener('recommendations-loaded', () => {
        this.initialized = false;
        this.currentIndex = 0;
        setTimeout(() => {
          this.initializeCarousel();
        }, 100);
      });
    }

    getChildWidth() {
      if (this.productItems.length === 0) return 0;
      return this.productItems[0].offsetWidth;
    }

    scrollProducts(direction) {
      const childWidth = this.getChildWidth();
      if (childWidth === 0) return;

      // Calculate how many items fit in the visible area
      const visibleItems = Math.floor(this.scroller.clientWidth / childWidth);
      const maxIndex = Math.max(0, this.productItems.length - visibleItems);

      if (direction === "prev") {
        this.currentIndex = Math.max(this.currentIndex - 1, 0);
      } else if (direction === "next") {
        this.currentIndex = Math.min(this.currentIndex + 1, maxIndex);
      }

      const scrollTo = this.currentIndex * childWidth;

      this.scroller.scrollTo({
        top: 0,
        left: scrollTo,
        behavior: "smooth",
      });

      this.updateButtonState();
      this.updateFadeOverlays();
    }

    updateButtonState() {
      const childWidth = this.getChildWidth();
      const visibleItems = Math.floor(this.scroller.clientWidth / childWidth);
      const maxIndex = Math.max(0, this.productItems.length - visibleItems);

      this.setButtonState(this.prevButton, this.currentIndex <= 0);
      this.setButtonState(this.nextButton, this.currentIndex >= maxIndex);
    }

    setButtonState(button, isDisabled) {
      if (!button) return;
      button.style.opacity = isDisabled ? "0.5" : "1";
      button.style.cursor = isDisabled ? "not-allowed" : "pointer";
    }

    updateFadeOverlays() {
      if (!this.container || !this.container.classList.contains('show-fade-overlays')) return;
      
      this.container.classList.remove('show-left-fade', 'show-right-fade');
      
      if (this.currentIndex > 0) {
        this.container.classList.add('show-left-fade');
      }
      
      const childWidth = this.getChildWidth();
      const visibleItems = Math.floor(this.scroller.clientWidth / childWidth);
      const maxIndex = Math.max(0, this.productItems.length - visibleItems);
      
      if (this.currentIndex < maxIndex) {
        this.container.classList.add('show-right-fade');
      }
    }

    setProductWidths(cardWidth) {
      const relatedProductItemWrapper = this.querySelectorAll('.related_product__item_wrapper');
      
      if (this.productItems.length <= 1) {
        relatedProductItemWrapper.forEach(item => {
          item.style.flex = '0 0 100%';
        });
      } else {
        relatedProductItemWrapper.forEach(item => {
          item.style.flex = `0 0 ${cardWidth}%`;
        });
      }
    }

    applyCardStyling(cardBorders, cardBackground, cardBorder, imageWidth, cardWidth) {
      // Apply styling to all cards
      const cardBackgrounds = this.querySelectorAll('.card-background');
      cardBackgrounds.forEach(card => {
        if (cardBackground) {
          card.style.setProperty('--card-bg', cardBackground);
          card.style.backgroundColor = cardBackground;
        }
        if (cardBorder) {
          card.style.setProperty('--border', cardBorder);
          card.style.borderColor = cardBorder;
        }
        if (cardBorders) {
          card.style.border = `1px solid ${cardBorder || '#eee'}`;
        }
      });
      
      // Apply image size styling
      const productImages = this.querySelectorAll('.related_product__image');
      productImages.forEach(image => {
        image.classList.remove('image-small', 'image-medium', 'image-large');
        image.classList.add(`image-${imageWidth}`);
      });
      
      // Apply flex styling and card border class
      const relatedProductItemWrapper = this.querySelectorAll('.related_product__item_wrapper');
      relatedProductItemWrapper.forEach(item => {
        item.style.flex = `0 0 ${cardWidth}%`;
        
        if (cardBorders) {
          item.classList.add('card-border');
          const cardBg = item.querySelector('.card-background');
          if (cardBg) cardBg.classList.add('card-border');
        } else {
          item.classList.remove('card-border');
          const cardBg = item.querySelector('.card-background');
          if (cardBg) cardBg.classList.remove('card-border');
        }
      });
    }
  }

  // Register the custom element
  customElements.define('related-products-carousel', RelatedProductsCarousel);
})();